import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { detectEmbedType } from '../../../pages/artist/components/EmbedPlayer';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userType?: string;
}

const FAN_LEVELS = [
  { value: 0, label: '全員公開', sublabel: 'ALL', icon: 'ri-global-line', color: '#00ff41', rgb: '0,255,65' },
  { value: 1, label: 'LV.1以上', sublabel: 'RESONATOR', icon: 'ri-seedling-line', color: '#4ade80', rgb: '74,222,128' },
  { value: 2, label: 'LV.2以上', sublabel: 'DEEP DIVER', icon: 'ri-fire-line', color: '#fb923c', rgb: '251,146,60' },
  { value: 3, label: 'LV.3以上', sublabel: 'CORE FAN', icon: 'ri-vip-crown-line', color: '#facc15', rgb: '250,204,21' },
];

export function CreatePostModal({ isOpen, onClose, onSuccess, userType = 'maker' }: CreatePostModalProps) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [requiredFanLevel, setRequiredFanLevel] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isNode = userType === 'node';
  const isMaker = userType === 'maker';
  const accentColor = isNode ? '#00d9ff' : '#00ff41';
  const accentRgb = isNode ? '0, 217, 255' : '0, 255, 65';

  const selectedLevel = FAN_LEVELS.find((l) => l.value === requiredFanLevel) ?? FAN_LEVELS[0];

  // メディアURLの種類を判定
  const mediaType = detectEmbedType(mediaUrl);
  const isVideoUrl = mediaType !== 'none';

  const handleFileSelect = useCallback((file: File) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('JPG, PNG, WebP, GIF のみ対応しています');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('ファイルサイズは10MB以下にしてください');
      return;
    }
    setError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !user) return null;
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', imageFile);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('セッションが見つかりません');

      // プログレスシミュレーション
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 15, 85));
      }, 200);

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upload-post-image`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: formData,
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error ?? 'アップロードに失敗しました');
      }

      const { url } = await res.json();
      return url as string;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    if (!content.trim()) {
      setError('本文を入力してください。');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let finalMediaUrl: string | null = null;

      if (uploadMode === 'file' && imageFile) {
        finalMediaUrl = await uploadImage();
      } else if (uploadMode === 'url' && mediaUrl.trim()) {
        finalMediaUrl = mediaUrl.trim();
      }

      const { error: insertError } = await supabase.from('artist_posts').insert({
        artist_id: user.id,
        content: content.trim(),
        media_url: finalMediaUrl,
        required_fan_level: isNode ? 0 : requiredFanLevel,
        status: 'published',
      });

      if (insertError) throw insertError;

      resetForm();
      onSuccess();
    } catch (err: unknown) {
      console.error('Failed to create post:', err);
      setError(err instanceof Error ? err.message : '投稿の作成に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setContent('');
    setMediaUrl('');
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setRequiredFanLevel(0);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (isSubmitting || isUploading) return;
    resetForm();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-4 top-[5vh] z-50 max-w-lg mx-auto rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(10, 10, 10, 0.98)',
              border: `1px solid rgba(${accentRgb}, 0.2)`,
              boxShadow: `0 0 60px rgba(${accentRgb}, 0.1)`,
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
              style={{ background: 'rgba(10,10,10,0.98)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: `rgba(${accentRgb}, 0.15)`, border: `1px solid rgba(${accentRgb}, 0.3)` }}
                >
                  <i className={`${isNode ? 'ri-store-2-line' : 'ri-edit-2-line'} text-sm`} style={{ color: accentColor }} />
                </div>
                <div>
                  <h2 className="text-white font-display text-lg tracking-wider">
                    {isNode ? '告知を投稿' : '新規投稿'}
                  </h2>
                  {isNode && (
                    <p className="text-xs tracking-widest mt-0.5" style={{ color: accentColor }}>
                      NODE — 全体公開
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-400 text-xl" />
              </button>
            </div>

            {/* NODE向けバナー */}
            {isNode && (
              <div
                className="mx-6 mt-5 px-4 py-3 rounded-xl flex items-center gap-3"
                style={{ background: `rgba(${accentRgb}, 0.08)`, border: `1px solid rgba(${accentRgb}, 0.2)` }}
              >
                <i className="ri-megaphone-line text-lg" style={{ color: accentColor }} />
                <p className="text-xs leading-relaxed" style={{ color: accentColor }}>
                  イベント情報・新入荷・営業時間変更など、空間からの告知を全リスナーに届けましょう。
                </p>
              </div>
            )}

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Content */}
              <div>
                <label className="block text-gray-400 text-xs tracking-widest uppercase mb-2">
                  {isNode ? '告知内容' : '本文'} <span style={{ color: accentColor }}>*</span>
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={isMaker ? 8 : 4}
                  maxLength={isMaker ? 1000 : 500}
                  placeholder={
                    isNode
                      ? 'イベントや入荷情報を告知...'
                      : 'ファンへのメッセージを入力...\n\nアーティストの方は最大1000文字まで、じっくりとライナーノーツを書くことができます。'
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm leading-relaxed placeholder-gray-600 focus:outline-none transition-colors resize-none"
                  onFocus={(e) => (e.currentTarget.style.borderColor = `rgba(${accentRgb}, 0.4)`)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-gray-600 text-xs">
                    {isMaker ? 'じっくりとライナーノーツを書けます' : ''}
                  </p>
                  <p className="text-right text-gray-600 text-xs">
                    {content.length} / {isMaker ? '1000' : '500'}
                  </p>
                </div>
              </div>

              {/* ── メディアセクション ── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-gray-400 text-xs tracking-widest uppercase">
                    メディア（任意）
                  </label>
                  {/* アップロードモード切替 */}
                  <div
                    className="flex items-center rounded-lg overflow-hidden"
                    style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {(['file', 'url'] as const).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => { setUploadMode(mode); removeImage(); setMediaUrl(''); }}
                        className="px-3 py-1.5 text-xs font-bold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap"
                        style={{
                          background: uploadMode === mode ? `rgba(${accentRgb}, 0.2)` : 'transparent',
                          color: uploadMode === mode ? accentColor : '#666',
                          borderRight: mode === 'file' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                        }}
                      >
                        {mode === 'file' ? (
                          <span className="flex items-center gap-1.5">
                            <i className="ri-upload-cloud-line" />
                            画像アップロード
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <i className="ri-link" />
                            画像・動画URL
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ファイルアップロードエリア */}
                {uploadMode === 'file' && (
                  <>
                    {imagePreview ? (
                      /* プレビュー表示 */
                      <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                        <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                        {/* アップロード中オーバーレイ */}
                        <AnimatePresence>
                          {isUploading && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                              style={{ background: 'rgba(0,0,0,0.7)' }}
                            >
                              <i className="ri-upload-cloud-2-line text-3xl" style={{ color: accentColor }} />
                              <p className="text-white text-sm font-bold tracking-wider">アップロード中...</p>
                              <div className="w-48 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <motion.div
                                  className="h-full rounded-full"
                                  style={{ background: accentColor }}
                                  animate={{ width: `${uploadProgress}%` }}
                                  transition={{ duration: 0.3 }}
                                />
                              </div>
                              <p className="text-xs" style={{ color: `rgba(${accentRgb}, 0.8)` }}>
                                {uploadProgress}%
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* 削除ボタン */}
                        {!isUploading && (
                          <button
                            onClick={removeImage}
                            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
                            style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,50,50,0.7)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.7)')}
                          >
                            <i className="ri-delete-bin-line text-white text-sm" />
                          </button>
                        )}
                        {/* ファイル名 */}
                        {!isUploading && (
                          <div
                            className="absolute bottom-0 left-0 right-0 px-3 py-2 flex items-center gap-2"
                            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}
                          >
                            <i className="ri-image-line text-xs text-white/60" />
                            <span className="text-xs text-white/60 truncate">{imageFile?.name}</span>
                            <span className="text-xs text-white/40 ml-auto whitespace-nowrap">
                              {imageFile ? `${(imageFile.size / 1024 / 1024).toFixed(1)} MB` : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* ドロップゾーン */
                      <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                        className="relative rounded-xl flex flex-col items-center justify-center gap-3 py-10 cursor-pointer transition-all duration-200"
                        style={{
                          border: `2px dashed ${isDragging ? accentColor : 'rgba(255,255,255,0.12)'}`,
                          background: isDragging ? `rgba(${accentRgb}, 0.06)` : 'rgba(255,255,255,0.02)',
                        }}
                      >
                        <motion.div
                          animate={isDragging ? { scale: 1.15 } : { scale: 1 }}
                          transition={{ duration: 0.2 }}
                          className="w-14 h-14 flex items-center justify-center rounded-full"
                          style={{
                            background: isDragging ? `rgba(${accentRgb}, 0.15)` : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${isDragging ? accentColor : 'rgba(255,255,255,0.1)'}`,
                          }}
                        >
                          <i
                            className="ri-image-add-line text-2xl"
                            style={{ color: isDragging ? accentColor : '#555' }}
                          />
                        </motion.div>
                        <div className="text-center">
                          <p className="text-sm font-bold tracking-wider" style={{ color: isDragging ? accentColor : '#888' }}>
                            {isDragging ? 'ここにドロップ' : 'クリックまたはドラッグ&ドロップ'}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">JPG, PNG, WebP, GIF — 最大10MB</p>
                        </div>
                        {isDragging && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="absolute inset-0 rounded-xl pointer-events-none"
                            style={{ border: `2px solid ${accentColor}`, boxShadow: `inset 0 0 30px rgba(${accentRgb}, 0.1)` }}
                          />
                        )}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleFileInputChange}
                    />
                  </>
                )}

                {/* URL入力モード */}
                {uploadMode === 'url' && (
                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        type="url"
                        value={mediaUrl}
                        onChange={(e) => { setMediaUrl(e.target.value); }}
                        placeholder="https://youtube.com/watch?v=... または https://example.com/image.jpg"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none transition-colors"
                        onFocus={(e) => (e.currentTarget.style.borderColor = `rgba(${accentRgb}, 0.4)`)}
                        onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                      />
                      {/* メディアタイプインジケーター */}
                      {mediaUrl && (
                        <div
                          className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2 py-1 rounded"
                          style={{
                            background: isVideoUrl ? 'rgba(255, 0, 110, 0.15)' : 'rgba(0, 255, 65, 0.15)',
                            border: `1px solid ${isVideoUrl ? 'rgba(255, 0, 110, 0.3)' : 'rgba(0, 255, 65, 0.3)'}`,
                          }}
                        >
                          <i
                            className={`text-xs ${isVideoUrl ? 'ri-movie-2-line' : 'ri-image-line'}`}
                            style={{ color: isVideoUrl ? '#ff006e' : '#00ff41' }}
                          />
                          <span
                            className="text-[10px] font-bold tracking-wider uppercase"
                            style={{ color: isVideoUrl ? '#ff006e' : '#00ff41' }}
                          >
                            {isVideoUrl ? mediaType.toUpperCase() : 'IMAGE'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* ヒント */}
                    <div
                      className="flex items-start gap-2 px-3 py-2 rounded-lg"
                      style={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                      }}
                    >
                      <i className="ri-information-line text-xs text-gray-500 mt-0.5" />
                      <p className="text-xs text-gray-500 leading-relaxed">
                        YouTube、TikTok、Spotify、SoundCloud、ニコニコ動画のURLを入力すると自動的にプレイヤーが埋め込まれます。画像URLの場合は画像として表示されます。
                      </p>
                    </div>

                    {/* URLプレビュー */}
                    <AnimatePresence>
                      {mediaUrl && !isVideoUrl && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="relative rounded-xl overflow-hidden"
                          style={{ aspectRatio: '16/9' }}
                        >
                          <img
                            src={mediaUrl}
                            alt="URL preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                          <div
                            className="absolute bottom-0 left-0 right-0 px-3 py-2"
                            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}
                          >
                            <p className="text-xs text-white/50 truncate">{mediaUrl}</p>
                          </div>
                        </motion.div>
                      )}
                      {mediaUrl && isVideoUrl && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="relative rounded-xl overflow-hidden p-4"
                          style={{
                            background: 'rgba(255, 0, 110, 0.05)',
                            border: '1px solid rgba(255, 0, 110, 0.2)',
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0"
                              style={{
                                background: 'rgba(255, 0, 110, 0.15)',
                                border: '1px solid rgba(255, 0, 110, 0.3)',
                              }}
                            >
                              <i className="ri-movie-2-fill text-xl" style={{ color: '#ff006e' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold tracking-wider" style={{ color: '#ff006e' }}>
                                動画プレイヤーが埋め込まれます
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{mediaUrl}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* ── ファンレベル設定（MAKERのみ） ── */}
              {!isNode && (
                <div>
                  <label className="block text-gray-400 text-xs tracking-widest uppercase mb-3">
                    公開対象ファンレベル
                  </label>

                  {/* レベル選択グリッド */}
                  <div className="grid grid-cols-2 gap-2">
                    {FAN_LEVELS.map((level) => {
                      const isSelected = requiredFanLevel === level.value;
                      return (
                        <motion.button
                          key={level.value}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setRequiredFanLevel(level.value)}
                          className="relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer text-left overflow-hidden"
                          style={{
                            background: isSelected ? `rgba(${level.rgb}, 0.12)` : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${isSelected ? `rgba(${level.rgb}, 0.5)` : 'rgba(255,255,255,0.07)'}`,
                            boxShadow: isSelected ? `0 0 16px rgba(${level.rgb}, 0.15)` : 'none',
                          }}
                        >
                          {/* 選択中グロー */}
                          {isSelected && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                background: `radial-gradient(ellipse at left center, rgba(${level.rgb}, 0.08) 0%, transparent 70%)`,
                              }}
                            />
                          )}
                          {/* アイコン */}
                          <div
                            className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                            style={{
                              background: isSelected ? `rgba(${level.rgb}, 0.2)` : 'rgba(255,255,255,0.05)',
                              border: `1px solid ${isSelected ? `rgba(${level.rgb}, 0.4)` : 'rgba(255,255,255,0.08)'}`,
                            }}
                          >
                            <i className={`${level.icon} text-sm`} style={{ color: isSelected ? level.color : '#555' }} />
                          </div>
                          {/* テキスト */}
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-xs font-black tracking-wider"
                              style={{ color: isSelected ? level.color : '#888' }}
                            >
                              {level.label}
                            </p>
                            <p
                              className="text-[10px] tracking-widest mt-0.5"
                              style={{ color: isSelected ? `rgba(${level.rgb}, 0.7)` : '#444' }}
                            >
                              {level.sublabel}
                            </p>
                          </div>
                          {/* チェックマーク */}
                          {isSelected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                              style={{ background: `rgba(${level.rgb}, 0.3)`, border: `1px solid rgba(${level.rgb}, 0.5)` }}
                            >
                              <i className="ri-check-line text-xs" style={{ color: level.color }} />
                            </motion.div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* 選択中レベルの説明 */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={requiredFanLevel}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.2 }}
                      className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg"
                      style={{
                        background: `rgba(${selectedLevel.rgb}, 0.06)`,
                        border: `1px solid rgba(${selectedLevel.rgb}, 0.2)`,
                      }}
                    >
                      <i className="ri-information-line text-xs" style={{ color: `rgba(${selectedLevel.rgb}, 0.8)` }} />
                      <p className="text-xs" style={{ color: `rgba(${selectedLevel.rgb}, 0.8)` }}>
                        {requiredFanLevel === 0
                          ? 'すべてのユーザーがこの投稿を閲覧できます'
                          : `ファンレベル ${requiredFanLevel}（${selectedLevel.sublabel}）以上のファンのみ閲覧できます`}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl"
                  style={{ background: 'rgba(255, 50, 50, 0.1)', border: '1px solid rgba(255, 50, 50, 0.3)' }}
                >
                  <i className="ri-error-warning-line text-red-400 text-sm" />
                  <p className="text-red-400 text-sm">{error}</p>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div
              className="px-6 py-4 flex items-center gap-3 sticky bottom-0"
              style={{ background: 'rgba(10,10,10,0.98)', borderTop: '1px solid rgba(255,255,255,0.05)' }}
            >
              <button
                onClick={handleClose}
                disabled={isSubmitting || isUploading}
                className="flex-1 py-3 rounded-xl text-gray-400 text-sm font-bold tracking-wider hover:bg-white/5 transition-colors cursor-pointer whitespace-nowrap"
                style={{ border: '1px solid rgba(255, 255, 255, 0.08)' }}
              >
                キャンセル
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={isSubmitting || isUploading || !content.trim()}
                className="flex-1 py-3 rounded-xl text-black text-sm font-bold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  background:
                    isSubmitting || isUploading || !content.trim()
                      ? `rgba(${accentRgb}, 0.3)`
                      : isNode
                      ? 'linear-gradient(135deg, #00d9ff 0%, #0099bb 100%)'
                      : 'linear-gradient(135deg, #00ff41 0%, #00cc33 100%)',
                  boxShadow:
                    isSubmitting || isUploading || !content.trim()
                      ? 'none'
                      : `0 0 20px rgba(${accentRgb}, 0.3)`,
                }}
              >
                {isSubmitting || isUploading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    {isUploading ? '画像アップロード中...' : '投稿中...'}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className={isNode ? 'ri-megaphone-fill' : 'ri-send-plane-fill'} />
                    {isNode ? '告知を配信する' : '投稿する'}
                  </span>
                )}
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
